import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plug, Users, Shield, X } from "lucide-react";

const PROVIDERS = [
  { kind: "stripe",        name: "Stripe (payments)",       fields: ["secret_key", "publishable_key", "webhook_secret"] },
  { kind: "razorpay",      name: "Razorpay (payments)",     fields: ["key_id", "key_secret"] },
  { kind: "twilio_sms",    name: "Twilio (SMS)",            fields: ["account_sid", "auth_token", "from_number"] },
  { kind: "whatsapp",      name: "WhatsApp Business API",   fields: ["phone_number_id", "access_token"] },
  { kind: "sendgrid",      name: "SendGrid (email)",        fields: ["api_key", "from_email"] },
  { kind: "resend",        name: "Resend (email)",          fields: ["api_key", "from_email"] },
  { kind: "google_maps",   name: "Google Maps",             fields: ["api_key"] },
  { kind: "cloud_storage", name: "Cloud storage",           fields: ["bucket", "access_key", "secret"] },
  { kind: "sap",           name: "SAP",                     fields: ["base_url", "client", "username", "password"] },
  { kind: "tally",         name: "Tally Prime",             fields: ["ngrok_url", "company"] },
];

function ProviderCard({ p, value, roles, users, onSave }) {
  const [form, setForm] = useState(value?.config || {});
  const [active, setActive] = useState(value?.active ?? false);
  const [allowedRoles, setAllowedRoles] = useState(value?.allowed_roles || []);
  const [allowedUserIds, setAllowedUserIds] = useState(value?.allowed_user_ids || []);
  const [userSearch, setUserSearch] = useState("");

  const toggleRole = (r) => setAllowedRoles((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  const toggleUser = (id) => setAllowedUserIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const eligibleUsers = useMemo(() => {
    if (!allowedRoles.length) return [];
    const s = userSearch.toLowerCase();
    return users
      .filter((u) => allowedRoles.includes(u.role))
      .filter((u) => !s || (u.email || "").toLowerCase().includes(s) || (u.full_name || "").toLowerCase().includes(s));
  }, [users, allowedRoles, userSearch]);

  const selectedUsers = useMemo(
    () => users.filter((u) => allowedUserIds.includes(u.id)),
    [users, allowedUserIds]
  );

  return (
    <Card data-testid={`integration-${p.kind}`} className="overflow-hidden">
      <CardContent className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Plug className="w-4 h-4" />
            <span className="font-semibold">{p.name}</span>
          </div>
          <div className="flex items-center gap-2">
            {value?.active ? <Badge>Active</Badge> : <Badge variant="outline">Off</Badge>}
            <Switch checked={active} onCheckedChange={setActive} data-testid={`integration-${p.kind}-toggle`} />
          </div>
        </div>

        {/* Credentials */}
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-orange-600 font-bold">Credentials</div>
          {p.fields.map((f) => (
            <div key={f}>
              <Label className="text-xs">{f}</Label>
              <Input
                type={f.includes("secret") || f.includes("password") ? "password" : "text"}
                value={form[f] || ""}
                onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                data-testid={`integration-${p.kind}-${f}`}
              />
            </div>
          ))}
        </div>

        {/* Scope · Roles */}
        <div className="space-y-2 border-t border-border pt-4">
          <div className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-emerald-600" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Access · Roles</div>
          </div>
          <p className="text-[11px] text-muted-foreground">Pick which roles are allowed to use this integration. Super Admin always has access.</p>
          <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto pr-1">
            {roles.map((r) => (
              <label key={r} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-accent/60 rounded px-2 py-1" data-testid={`integration-${p.kind}-role-${r}`}>
                <Checkbox
                  checked={allowedRoles.includes(r)}
                  onCheckedChange={() => toggleRole(r)}
                />
                <span className="font-mono text-[11px]">{r}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Scope · Users (only if some role picked) */}
        {allowedRoles.length ? (
          <div className="space-y-2 border-t border-border pt-4">
            <div className="flex items-center gap-2">
              <Users className="w-3.5 h-3.5 text-blue-600" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-blue-700 font-bold">Access · Specific users (optional)</div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Leave empty to grant access to <b>all users of the picked roles</b>. Pick individual users to restrict further.
            </p>

            {selectedUsers.length ? (
              <div className="flex flex-wrap gap-1">
                {selectedUsers.map((u) => (
                  <Badge key={u.id} variant="secondary" className="text-[10px] gap-1">
                    {u.email}
                    <button onClick={() => toggleUser(u.id)} className="ml-0.5 hover:text-red-600" data-testid={`integration-${p.kind}-user-remove-${u.id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : null}

            <Input
              placeholder="Search email or name…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              data-testid={`integration-${p.kind}-user-search`}
              className="text-xs h-8"
            />
            <div className="max-h-40 overflow-y-auto border border-border rounded-md divide-y divide-border">
              {eligibleUsers.length === 0 ? (
                <div className="text-[11px] text-muted-foreground text-center py-3">
                  {userSearch ? "No matching users." : "Users matching the selected roles will appear here."}
                </div>
              ) : (
                eligibleUsers.slice(0, 60).map((u) => (
                  <label key={u.id} className="flex items-start gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-accent/60" data-testid={`integration-${p.kind}-user-${u.id}`}>
                    <Checkbox
                      checked={allowedUserIds.includes(u.id)}
                      onCheckedChange={() => toggleUser(u.id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{u.full_name || u.email}</div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {u.email} · <span className="font-mono">{u.role}</span>{u.tenant_name ? ` · ${u.tenant_name}` : ""}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        ) : null}

        <div className="text-right pt-2 border-t border-border">
          <Button
            size="sm"
            onClick={() => onSave({
              kind: p.kind,
              name: p.name,
              config: form,
              active,
              allowed_roles: allowedRoles,
              allowed_user_ids: allowedUserIds,
            })}
            data-testid={`integration-${p.kind}-save`}
          >
            Save {p.name}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Integrations() {
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);

  const load = async () => {
    const [ints, scope] = await Promise.all([
      api.get("/admin/integrations"),
      api.get("/admin/integrations/scope-options"),
    ]);
    setItems(ints.data);
    setRoles(scope.data.roles);
    setUsers(scope.data.users);
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try {
      await api.post("/admin/integrations", payload);
      toast.success(`${payload.name} saved`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const byKind = Object.fromEntries(items.map((i) => [i.kind, i]));

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-integrations">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Integrations</h1>
        <p className="text-muted-foreground">
          Configure payment gateways, messaging, maps, cloud storage, ERP/CRM connectors and webhooks.
          Every integration is scoped — pick the roles and (optionally) the specific users who are allowed to use it.
          Secrets never appear in audit logs.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {PROVIDERS.map((p) => (
          <ProviderCard
            key={p.kind}
            p={p}
            value={byKind[p.kind]}
            roles={roles}
            users={users}
            onSave={save}
          />
        ))}
      </div>
    </div>
  );
}
